import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import { Form, Formik } from "formik";
import ComboBox from "../../common/ComboBox";
import TextBox from "../../common/TextBox";
import Button from "../../common/Button";

import { ReactComponent as EmptyFolderSvg } from "../../../assets/admin/empty-folder.svg";
import useLandingPage from "../../../hooks/useLandingPage";
import { PAGE_STATE } from "../../../constants";
import useWorks from "../../../hooks/useWorks";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import DatePicker from "../../common/DatePicker";
import { startOfDay } from "date-fns";
import { toast } from "react-toastify";

const Add = ({ section, editing: _editing, onClose, onSuccess }) => {
  const [selectedWork, setSelectedWork] = useState(undefined);
  const [timeoutId, setTimeoutId] = useState(undefined);
  const [editing] = useState(_editing);
  const [pageState, setPageState] = useState(
    editing ? PAGE_STATE.LOADING : PAGE_STATE.IDLE
  );
  const [initialValues, setInitialValues] = useState({
    work: "",
    price: "",
    effectiveDate: startOfDay(new Date()),
    dueDate: startOfDay(new Date()),
  });
  const formikRef = useRef(null);

  const {
    getLandingPageConfig,
    createLandingPageConfig,
    patchLandingPageConfig,
  } = useLandingPage();
  const { queryWorks, getWork } = useWorks();
  const { queryUsers, getUser } = useUsers();
  const { plans } = useContext(AppContext);

  useEffect(() => {
    if (!editing) return;
    fetchData();
    return () => {};
  }, []);

  const fetchData = async () => {
    const { work, price, effectiveDate, dueDate } = await getLandingPageConfig(
      editing
    );

    const _work = await getWork(work);
    const _user = await getUser(_work.createdBy);

    setSelectedWork({
      ..._work,
      owner: _user,
    });
    setInitialValues({
      work,
      price,
      effectiveDate: new Date(effectiveDate),
      dueDate: new Date(dueDate),
    });
    setPageState(PAGE_STATE.IDLE);
  };

  const handleValidate = ({ work, price, effectiveDate, dueDate }) => {
    // * Clear timeout first if present
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    let errors = {};

    if (!work) {
      errors.work = "Specify Project ID";
      setSelectedWork(undefined);
    }
    if (!price) errors.price = "Cannot leave blank";
    if (!effectiveDate) errors.effectiveDate = "Specify starting date";
    if (!dueDate) errors.dueDate = "Specify ending date";

    if (work) {
      // * Get work to display if found
      let _timeoutId = setTimeout(async () => {
        // TODO: Filter and get works
        try {
          const { data: works } = await queryWorks({
            _id: work,
          });

          if (works.length === 0) {
            formikRef.current.setFieldError("work", "Work not found");
          }

          const { data: users } = await queryUsers({
            _id: works[0].createdBy,
          });

          const _work = {
            ...works[0],
            owner: users[0],
          };

          setSelectedWork(_work);
        } catch (error) {
          formikRef.current.setFieldError("work", "Work not found");
        }
      }, 1000);

      setTimeoutId(_timeoutId);
    }

    if (effectiveDate > dueDate) {
      errors.effectiveDate = "Can't come after ending date";
      errors.dueDate = "Can't come before starting date";
    }

    return errors;
  };

  const handleSubmit = async (
    { work, price, effectiveDate, dueDate },
    options
  ) => {
    if (editing) {
      try {
        await patchLandingPageConfig(editing, {
          section,
          work,
          price: +price,
          effectiveDate,
          dueDate,
        });
        toast.success(`Update landing page config Success`);
        onSuccess();
      } catch (error) {
        toast.success(`Unable to update landing page config!`);
      }
      return;
    }

    try {
      // TODO: Check if work exist during same period
      await createLandingPageConfig({
        section,
        work,
        price: +price,
        effectiveDate,
        dueDate,
        priorities: [],
        category: 1,
      });
      toast.success(`Create landing page config Success`);
      onSuccess();
    } catch (error) {
      toast.success(`Unable to create landing page config!`);
    }
    return;
  };

  const displayWork = () => {
    const {
      banner,
      name,
      owner: { avatar, firstname, surname, plan },
    } = selectedWork;

    return (
      <div className="admin-lp-hb-add-form-work-selected">
        <img
          className="admin-lp-hb-add-form-work-selected__image"
          alt="banner"
          src={banner}
        />
        <div className="admin-lp-hb-add-form-work-selected-owner">
          <img
            className="admin-lp-hb-add-form-work-selected-owner__avatar"
            alt="profile"
            src={avatar}
          />
          <label className="admin-lp-hb-add-form-work-selected-owner__work-name">
            {name}
          </label>
          <label className="admin-lp-hb-add-form-work-selected-owner__owner-name">{`${firstname} ${
            surname || ""
          }`}</label>
          <label className="admin-lp-hb-add-form-work-selected-owner__owner-plan">
            {plans.find((p) => p._id === plan).name}
          </label>
        </div>
      </div>
    );
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <Overlay />;
  }

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      onClose={onClose}
      content={
        <div className="admin-lp-hb-add">
          <h1 className="admin-lp-hb-add__title">
            {editing ? "View Banner" : "Add Banner"}
          </h1>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validate={handleValidate}
            onSubmit={handleSubmit}
          >
            {(props) => {
              return (
                <Form className="admin-lp-hb-add-form">
                  <TextBox label="Work to display" name="work" />
                  <TextBox
                    label="Price"
                    name="price"
                    placeholder="e.g. 100,000"
                    type="number"
                  />
                  <DatePicker
                    label="Start date"
                    name="effectiveDate"
                    dateFormat={"dd/MM/yyyy"}
                  />
                  <DatePicker
                    label="End date"
                    name="dueDate"
                    dateFormat={"dd/MM/yyyy"}
                  />
                  <div className="admin-lp-hb-add-form-work">
                    {selectedWork ? (
                      displayWork()
                    ) : (
                      <>
                        <EmptyFolderSvg className="admin-lp-hb-add-form-work__empty-image" />
                        <label className="admin-lp-hb-add-form-work__empty-text">
                          Specify Project ID
                        </label>
                      </>
                    )}
                  </div>
                  <div className="admin-lp-hb-add-form-footer">
                    <Button
                      label={"Cancel"}
                      variant={"secondary"}
                      onClick={onClose}
                    />
                    <div style={{ flexGrow: 1 }}></div>
                    {editing ? (
                      <Button label={"Save"} type="submit" />
                    ) : (
                      <Button label={"Add"} type="submit" />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      }
    />
  );
};

Add.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  editing: PropTypes.string,
};

Add.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
  editing: undefined,
};

export default Add;
